import React, { useState, useEffect, useContext } from "react";
import "./Complaint.css";
import { useTranslation } from "react-i18next";
import { _t, customStyle, getCookie } from "../../../../functions/Functions";
import { Helmet } from "react-helmet";
import ComplaintsSidebar from "./ComplaintsSidebar";
import { UserContext } from "../../../../contexts/User";
import { AuditContext } from "../../../../contexts/Audit";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";
import Moment from "react-moment";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const getElapsedString = (startTime) => {
  if (!startTime) return "0 min";
  const safeTime = typeof startTime === 'string' ? startTime.replace(" ", "T") : startTime;
  const start = new Date(safeTime);
  const now = new Date();
  const diffMs = now - start;
  if (diffMs < 0 || isNaN(diffMs)) return "0 min";

  const diffMins = Math.floor(diffMs / 60000);
  const days = Math.floor(diffMins / (24 * 60));
  const hours = Math.floor((diffMins % (24 * 60)) / 60);
  const mins = diffMins % 60;

  const parts = [];
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (mins > 0 || parts.length === 0) {
    parts.push(`${mins} min`);
  }

  return parts.join(" ");
};

const ComplaintBoard = () => {
  const { t } = useTranslation();
  
  const [boardState, setBoardState] = useState({
    filter: "all",
    filteredComplaints: [],
    branchId: "",
    startDate: "",
    endDate: "",
    stats: [],
    tabs: [],
    complaintsData: [],
    loading: false,
  });

  const { filter, filteredComplaints, branchId, startDate, endDate, stats, tabs, complaintsData, loading } = boardState;

  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const updateState = (updates) => {
    setBoardState((prev) => ({ ...prev, ...updates }));
  };

  const { authUserInfo } = useContext(UserContext);
  const { loading: auditLoading } = useContext(AuditContext);
  const { branchForSearch } = useContext(RestaurantContext);

  const isMounted = React.useRef(true);

  useEffect(() => {
    fetchLiveBoardData();
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line
  }, []);

  const fetchLiveBoardData = async () => {
    updateState({ loading: true });
    try {
      const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const payload = {} ;
      if (Array.isArray(branchId) && branchId.length > 0) {
        payload.branch_id = branchId.map(b => b.id);
      } else if (typeof branchId === 'string' && branchId) {
        payload.branch_id = branchId;
      }
      if (startDate) payload.start_date = typeof startDate === 'string' ? startDate : formatDate(startDate);
      if (endDate) payload.end_date = typeof endDate === 'string' ? endDate : formatDate(endDate);

      const url = `${BASE_URL}/complaints/live-board`;
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` }
      });
      
      const { summary, tabs: resTabs, data } = response.data;
      
      let stateUpdates = {};
      
      if (summary) {
        stateUpdates.stats = summary;
      }
      if (resTabs) {
        stateUpdates.tabs = resTabs;
        if (resTabs.length > 0 && !resTabs.find(t => t.id === filter)) {
          stateUpdates.filter = resTabs[0].id;
        }
      }
      if (data) {
        stateUpdates.complaintsData = data;
      }
      
      if (isMounted.current) {
        updateState(stateUpdates);
      }
      
    } catch (error) {
      console.error("Error fetching live board data", error);
    } finally {
      if (isMounted.current) {
        updateState({ loading: false });
      }
    }
  };

  useEffect(() => {
    if (filter === "all" || filter === "All") {
      updateState({ filteredComplaints: complaintsData });
    } else if (filter === "guest_still_seated") {
      updateState({ filteredComplaints: complaintsData.filter((c) => c.guest_seated === true) });
    } else {
      updateState({ filteredComplaints: complaintsData.filter((c) => c.category_ids && c.category_ids.includes(filter)) });
    }
  }, [filter, complaintsData]);

  const getSlaColor = (createdAt) => {
    if (!createdAt) return "#1e293b";
    const safeTime = typeof createdAt === 'string' ? createdAt.replace(" ", "T") : createdAt;
    const start = new Date(safeTime);
    const now = new Date();
    const diffMs = now - start;
    if (diffMs < 0) return "#0a8d6e"; // Green

    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins >= 250) return "#dc3545"; // Red
    if (diffMins >= 180) return "#e85d04"; // Dark Orange
    if (diffMins >= 120) return "#fd7e14"; // Orange
    if (diffMins >= 60) return "#ffc107"; // Yellow
    if (diffMins >= 30) return "#0a8d6e"; // Green
    
    return "#0a8d6e"; // Default
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Live Complaint Board"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ComplaintsSidebar />
            </div>

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <CustomSpinLoader loading={loading || auditLoading || authUserInfo.permissions === null} />
              <div
                className="fk-scroll--pos-menu table-bottom-info-hide"
                data-simplebar
              >
                <div className="board-container">
                <div className="board-header">
                  <div className="board-title col-12 col-xl-5">
                    <h1 className="mb-1">{_t(t("Live complaint board"))}</h1>
                    <p className="mb-0">{_t(t("Duty manager – timer-based, KDS style"))}</p>
                  </div>
                  <div className="col-12 col-xl-7 d-flex align-items-center justify-content-xl-end mt-3 mt-xl-0 p-0">
                    <div className="mr-2" style={{ width: "120px" }}>
                      <DatePicker
                        selected={startDate ? new Date(startDate) : null}
                        onChange={(date) => updateState({ startDate: date })}
                        className="form-control py-1 px-2"
                        placeholderText={_t(t("Start Date"))}
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>
                    
                    <div className="mr-2" style={{ width: "120px" }}>
                      <DatePicker
                        selected={endDate ? new Date(endDate) : null}
                        onChange={(date) => updateState({ endDate: date })}
                        className="form-control py-1 px-2"
                        placeholderText={_t(t("End Date"))}
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>

                    <div className="mr-2" style={{ width: "200px" }}>
                      <Select
                        options={branchForSearch || []}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        isMulti
                        isClearable
                        value={Array.isArray(branchId) ? branchId : []}
                        onChange={(selected) => updateState({ branchId: selected || [] })}
                        styles={customStyle}
                        classNamePrefix="Branch"
                        maxMenuHeight="13rem"
                        placeholder={_t(t("All Branches"))}
                      />
                    </div>

                    <div>
                      <button className="btn btn-primary btn-sm px-3 rounded" onClick={fetchLiveBoardData}>
                        {_t(t("Load"))}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="stats-grid mt-2">
                  {(Array.isArray(stats) ? stats : []).map((stat, index) => (
                    <div className="stat-card" key={index}>
                      <div className="stat-title">{_t(t(stat.label || stat.name))}</div>
                      <div>
                        <div className="stat-value" style={{ color: stat.color || "#1e293b" }}>{stat.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Filters */}
                <div className="filter-row">
                  {(Array.isArray(tabs) ? tabs : []).map((tab) => (
                    <button
                      key={tab.id}
                      className={`filter-btn ${filter === tab.id ? "active" : ""}`}
                      onClick={() => updateState({ filter: tab.id })}
                    >
                      {_t(t(tab.label || tab.name))}
                    </button>
                  ))}
                </div>

                {/* Complaint Cards Grid */}
                <div className="cards-grid">
                  {(Array.isArray(filteredComplaints) ? filteredComplaints : []).map((complaint, index) => (
                    <div className="complaint-card" key={complaint.id || index}>
                      <div
                        className="card-top-border"
                        style={{ backgroundColor: getSlaColor(complaint.created_at) }}
                      ></div>
                      <div className="card-body">
                        <div className="card-header-flex">
                          <span className="complaint-id">{complaint.ticket_no || complaint.id}</span>
                          <span
                            className="complaint-timer"
                            style={{ color: getSlaColor(complaint.created_at) }}
                          >
                            {complaint.created_at ? (
                              <span>{getElapsedString(complaint.created_at)}</span>
                            ) : (
                              <span>{complaint.timer}</span>
                            )}
                          </span>
                        </div>
                        <div className="complaint-title">{complaint.party?.name || complaint.sub_category} {complaint?.table_name && complaint?.table_name !== "-" ? ` - ${complaint?.table_name}` : ""}</div>

                        <div className="complaint-desc">
                          {complaint.remarks}
                        </div>

                        {(complaint.status || complaint.statusColor) && (
                          <div
                            className="status-badge"
                            style={{ backgroundColor: complaint.statusColor || "#1e293b" }}
                          >
                            {_t(t(complaint.status_label || complaint.status))}
                          </div>
                        )}

                        <div className="card-footer">
                          <div className="footer-left">
                            {complaint.waiter_name && complaint.waiter_name !== "-" ? (
                              <span>{_t(t("Waiter"))}: <span>{complaint.waiter_name}</span></span>
                            ) : complaint.rider_name && complaint.rider_name !== "-" ? (
                              <span>{_t(t("Rider"))}: <span>{complaint.rider_name}</span></span>
                            ) : complaint?.customer_name ? (
                              <span>{_t(t("Customer"))}: <span>{complaint.customer_name}</span></span>
                            ) : null}
                          </div>
                          <div className="footer-right">
                            {complaint.priority ? <span>{_t(t(complaint.severity_label))}</span> : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Info Bar */}
                <div className="info-bar">
                  <strong>{_t(t("The timer is the whole point."))}</strong> {_t(t("Just like a Kitchen Display — a ticket's colour changes as time passes. The manager doesn't need to read the list, just spot the red ticket. Each complaint type's own SLA is set from the Setup screen."))}
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ComplaintBoard;
